import { useContext, createContext, useState, useCallback, useEffect, useRef } from 'react'
import { jwtDecode } from 'jwt-decode'
import axios from 'axios'
import { API_URL } from '../constants/api'

const AuthContext = createContext(null)

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

const isExpired = (decoded) => decoded?.exp && decoded.exp < Math.floor(Date.now() / 1000)
const save  = (token) => { localStorage.setItem('sp_token', token); localStorage.setItem('sp_auth', '1') }
const clear = ()      => { localStorage.removeItem('sp_token'); localStorage.removeItem('sp_auth') }

export function AuthProvider({ children }) {
  const [token,     setToken]     = useState(null)
  const [seller,    setSeller]    = useState(null)   // decoded JWT payload
  const [isLoading, setIsLoading] = useState(true)
  const visRef = useRef(document.visibilityState)

  const clearSession = useCallback(() => {
    clear()
    setSeller(null)
    setToken(null)
  }, [])

  const load = useCallback(() => {
    try {
      const raw = localStorage.getItem('sp_token')
      if (!raw) { setSeller(null); setToken(null); return }
      let decoded
      try { decoded = jwtDecode(raw) } catch { clearSession(); return }
      if (isExpired(decoded)) { clearSession(); return }
      // Only seller or admin roles can access the portal
      if (!['seller', 'admin', 'superadmin'].includes(decoded.role)) { clearSession(); return }
      setSeller(decoded)
      setToken(raw)
    } finally { setIsLoading(false) }
  }, [clearSession])

  useEffect(() => {
    load()
    const onVis = () => {
      if (visRef.current === 'hidden' && document.visibilityState === 'visible') load()
      visRef.current = document.visibilityState
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [load])

  useEffect(() => {
    const req = api.interceptors.request.use(cfg => {
      if (token) cfg.headers.Authorization = `Bearer ${token}`
      return cfg
    })
    const res = api.interceptors.response.use(r => r, err => {
      if (err.response?.status === 401) clearSession()
      return Promise.reject(err)
    })
    return () => { api.interceptors.request.eject(req); api.interceptors.response.eject(res) }
  }, [token, clearSession])

  const login = useCallback((rawToken) => {
    try {
      const decoded = jwtDecode(rawToken)
      if (isExpired(decoded)) return false
      if (!['seller', 'admin', 'superadmin'].includes(decoded.role)) return false
      save(rawToken)
      setToken(rawToken)
      setSeller(decoded)
      return true
    } catch { return false }
  }, [])

  const logout = useCallback(async () => {
    await api.post('/user/logout').catch(() => {})
    clearSession()
  }, [clearSession])

  return (
    <AuthContext.Provider value={{ seller, token, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}
